// Bike physics model (Martin et al., 1998): gravity, rolling resistance, aerodynamic drag, wheel inertia.
// On top come the aerodynamic shelter (drafting), the frontal area by build and the corner speed limit.
// Validated by packages/core/test/physics.test.ts against reference speeds and climb times (docs/PHYSICS.md).
export const G = 9.81, RHO = 1.2, CRR = 0.004, BIKE_KG = 8, CDA = 0.32;
/** Wheel inertia expressed as a fraction of the total mass. */
export const INERTIA = 1.04;
/** Speed ceiling (m/s, ~97 km/h): numerical safeguard, never reached in normal riding. */
export const V_MAX = 27;
/** Maximum lean angle in a corner (rad): ~42°, what a road tyre holds on dry ground. */
export const MAX_LEAN = (42 * Math.PI) / 180;
/** Braking deceleration before a corner (m/s²): firm without being an emergency stop. */
export const BRAKE = 4;

export const clamp = (x: number, a: number, b: number): number => Math.max(a, Math.min(b, x));

/** Total resistive force (N) at speed v (m/s) on a gradient `grade` (fraction). */
export function resistance(v: number, grade: number, mass: number, cda = CDA): number {
  const th = Math.atan(grade);
  return mass * G * (Math.sin(th) + CRR * Math.cos(th)) + 0.5 * RHO * cda * v * v;
}

/**
 * New speed after dt seconds at power P (W). `vmax` is the speed allowed at that spot
 * (corner): above it, the rider brakes by at most BRAKE m/s² without dropping below the limit.
 */
export function integrateSpeed(v: number, power: number, grade: number, mass: number, cda: number, dt: number, vmax = V_MAX): number {
  const F = resistance(v, grade, mass, cda);
  // below 1.5 m/s the driving force is capped: P/v would diverge from a standing start
  const a = (power / Math.max(v, 1.5) - F) / (mass * INERTIA);
  let next = v + a * dt;
  if (next > vmax) next = Math.max(vmax, Math.min(next, v - BRAKE * dt));
  return clamp(next, 0, V_MAX);
}

/** Steady-state speed (m/s) for a given power, by bisection. */
export function steadySpeed(power: number, grade: number, mass: number, cda = CDA): number {
  let lo = 0, hi = 30;
  for (let i = 0; i < 40; i++) { const m = (lo + hi) / 2; if (resistance(m, grade, mass, cda) * m > power) hi = m; else lo = m; }
  return (lo + hi) / 2;
}

// ---- air ----
/** Air density relative to sea level (standard atmosphere): ~0.82 at 2,000 m, which lightens the drag. */
export function airFactor(altitude: number): number {
  return (1 - 2.25577e-5 * clamp(altitude, -500, 9000)) ** 4.2559;
}

// ---- posture ----
// The rider changes position without thinking: tucked on a descent when barely pedalling, sitting up
// with hands on top of the bars on a steep slope at low speed. Factors applied to CdA (Kyle, Lukes et al.).
export const POSE = { tuck: 0.82, upright: 1.1, lag: 0.8 };

/** Target posture (CdA factor) from the gradient, speed (m/s) and effort relative to FTP. */
export function postureTarget(grade: number, v: number, ftpFrac: number): number {
  if (grade < -0.02 && v > 12 && ftpFrac < 0.4) return POSE.tuck;
  if (grade > 0.05 && v < 6.5 && ftpFrac < 1.3) return POSE.upright;
  return 1;
}

/** Smooth transition towards the target posture (time constant POSE.lag). */
export function postureStep(pose: number, target: number, dt: number): number {
  return pose + (target - pose) * Math.min(1, dt / POSE.lag);
}

// ---- build ----
// Frontal area from Bassett et al. (1999): A = 0.0293·h^0.725·m^0.425 + 0.0604 (bike included). The profile only gives
// the mass: height is estimated by similarity (h ∝ m^1/3, 1.78 m at 75 kg). CDA remains the value at 75 kg.
const frontal = (m: number): number => 0.0293 * (1.78 * Math.cbrt(m / 75)) ** 0.725 * m ** 0.425 + 0.0604;

/** A rider's CdA (m²) from their mass: a heavier build catches more wind, but less than proportionally. */
export function cdaFor(mass: number): number {
  return (CDA * frontal(clamp(mass || 75, 30, 200))) / frontal(75);
}

// ---- corners ----
/**
 * Chord: length factor of a path shifted by `offset` m (positive towards the centre when the signed
 * curvature `curv` (1/m) is positive). Below 1 on the inside: less distance, but a tighter radius.
 */
export function laneFactor(curv: number, offset: number): number {
  return clamp(1 - curv * offset, 0.7, 1.3);
}

/** Maximum speed (m/s) in a corner of radius r (m) at lean angle MAX_LEAN. */
export function cornerSpeed(radius: number): number {
  return Math.min(V_MAX, Math.sqrt(Math.tan(MAX_LEAN) * G * Math.max(0, radius)));
}

// ---- drafting ----
// Wind-tunnel measurements and simulations (Blocken et al. 2013 and 2018, Barry et al. 2015): ~35% less drag
// wheel to wheel, decreasing with the gap; the lead rider gains ~3% when followed closely; in the middle of a
// group the shelter accumulates. Distances are in metres, bike axis to bike axis.
/** Length of a bike (m): closer than this, the wheels overlap. */
export const BIKE_LEN = 1.7;
export const DRAFT = { peak: 0.35, decay: 4.5, width: 0.9, lead: 0.03, leadDecay: 1.5, max: 0.55, falloff: 0.55, range: 25 };

/** Share of shelter (0–1) by overlap: none side by side, full at one bike length. */
const overlap = (gap: number): number => clamp((gap - 0.9) / (BIKE_LEN - 0.9), 0, 1);
const lateral = (side: number): number => Math.exp(-((side / DRAFT.width) ** 2));

/** Drag reduction behind a rider `gap` m ahead and offset by `side` m. */
export function wakeShelter(gap: number, side: number): number {
  if (gap <= 0.9 || gap > DRAFT.range) return 0;
  return DRAFT.peak * Math.exp(-Math.max(0, gap - BIKE_LEN) / DRAFT.decay) * lateral(side) * overlap(gap);
}

/** Drag reduction for the lead rider when another follows `gap` m behind, offset by `side` m. */
export function pushShelter(gap: number, side: number): number {
  if (gap <= 0.9 || gap > 6) return 0;
  return DRAFT.lead * Math.exp(-Math.max(0, gap - BIKE_LEN) / DRAFT.leadDecay) * lateral(side) * overlap(gap);
}

/** Total shelter: the best shelter counts in full, each next one counts less (riders further back in the group). */
export function combineShelter(parts: number[], lead = 0): number {
  const s = parts.filter(p => p > 0).sort((a, b) => b - a);
  let keep = 1, w = 1;
  for (const p of s) { keep *= 1 - p * w; w *= DRAFT.falloff; }
  return 1 - (1 - Math.min(DRAFT.max, 1 - keep)) * (1 - lead);
}

/** Gradient equivalent (fraction) to the drag saved: what the shelter removes from the trainer's resistance. */
export function shelterGrade(v: number, shelter: number, mass: number, cda = CDA): number {
  return (0.5 * RHO * cda * v * v * clamp(shelter, 0, 1)) / (mass * G);
}
